import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Loader2, Megaphone, Check, ChevronRight, FileText, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const AUTH_HEADER = () => {
    const token = localStorage.getItem('valnexo_token') || localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
};

/**
 * Widget "Directives Direction" — Phase 1
 * Affiche les directives actives NON-ackées pour l'utilisateur courant.
 * Chaque directive a :
 *   - Titre + message perso
 *   - Bloc "Message Direction" (encart séparé si présent)
 *   - Bouton "Voir mes X dossiers concernés" → marque `viewed`
 *   - Bouton "J'ai lu et compris" (désactivé tant que non-viewed si dossiers)
 *   - Progression : "3/8 dossiers déjà traités"
 */
export default function DirectivesWidget() {
    const navigate = useNavigate();
    const [directives, setDirectives] = useState([]);
    const [loading, setLoading] = useState(true);
    const [ackingId, setAckingId] = useState(null);
    const [viewedIds, setViewedIds] = useState(new Set());

    const fetchDirectives = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/api/directives/mine`, {
                headers: AUTH_HEADER(),
                timeout: 15000,
            });
            setDirectives(res.data?.directives || []);
            // Auto-alimente le set des déjà-viewed
            const already = new Set(
                (res.data?.directives || [])
                    .filter((d) => d.viewed_at)
                    .map((d) => d.delivery_id)
            );
            setViewedIds(already);
        } catch {
            // silencieux — le widget disparaît proprement si backend indisponible
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchDirectives();
        // rafraîchissement doux toutes les 2 min pour capter les acks admin
        const t = setInterval(fetchDirectives, 120_000);
        return () => clearInterval(t);
    }, [fetchDirectives]);

    const markViewed = async (directive) => {
        try {
            await axios.post(
                `${API}/api/directives/${directive.mission_id}/mark-viewed`,
                {},
                { headers: AUTH_HEADER(), timeout: 10000 },
            );
            setViewedIds((prev) => new Set([...prev, directive.delivery_id]));
        } catch { /* silencieux */ }
    };

    const openMyProspects = async (directive) => {
        await markViewed(directive);
        const ids = (directive.prospect_ids || []).join(',');
        if (ids) {
            navigate(`/prospects?ids=${ids}&label=${encodeURIComponent('Directive · ' + directive.title)}`);
        } else {
            toast.info('Directive informative — aucun dossier spécifique à traiter.');
        }
    };

    const ackDirective = async (directive) => {
        setAckingId(directive.delivery_id);
        try {
            await axios.post(
                `${API}/api/directives/${directive.mission_id}/ack`,
                {},
                { headers: AUTH_HEADER(), timeout: 10000 },
            );
            toast.success('Directive accusée réception ✅');
            // Retire de la liste locale
            setDirectives((prev) => prev.filter((d) => d.delivery_id !== directive.delivery_id));
        } catch (e) {
            toast.error('Impossible d\'accuser réception — réessaye.');
        } finally {
            setAckingId(null);
        }
    };

    if (loading || directives.length === 0) return null;

    return (
        <Card data-testid="directives-widget" className="border-purple-300 bg-gradient-to-br from-purple-50 to-indigo-50/40">
            <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                    <Megaphone className="w-5 h-5 text-purple-700" />
                    <span>Directives Direction</span>
                    <Badge className="ml-1 bg-purple-700 text-white">{directives.length}</Badge>
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 pt-0">
                {directives.map((d) => {
                    const hasProspects = (d.prospect_count || 0) > 0;
                    const isViewed = viewedIds.has(d.delivery_id) || d.status === 'viewed' || d.status === 'acked';
                    const canAck = !hasProspects || isViewed;
                    const isAcking = ackingId === d.delivery_id;
                    const progressPct = hasProspects && d.prospect_count > 0
                        ? Math.round((d.prospect_count_moved / d.prospect_count) * 100)
                        : null;
                    return (
                        <div
                            key={d.delivery_id}
                            data-testid={`directive-card-${d.mission_id}`}
                            className="rounded-xl bg-white border border-purple-200 p-4 shadow-sm space-y-3"
                        >
                            <div className="flex items-start gap-2">
                                <div className="flex-1 min-w-0">
                                    <h4 className="font-semibold text-slate-900 text-sm">{d.title}</h4>
                                    <p className="text-xs text-slate-500 mt-0.5">
                                        Par {d.created_by_name || 'Direction'} · {new Date(d.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })}
                                    </p>
                                </div>
                                {d.status && d.status !== 'sent' && d.status !== 'displayed' && (
                                    <Badge variant="outline" className="text-[10px] shrink-0">
                                        {d.status === 'opened' && 'Ouverte'}
                                        {d.status === 'viewed' && 'Vue'}
                                        {d.status === 'acked' && '✓ Accusée'}
                                        {d.status === 'acted' && '⚡ Engagée'}
                                    </Badge>
                                )}
                            </div>

                            {/* Message perso */}
                            {d.personalized_message && (
                                <p className="text-sm text-slate-800 leading-relaxed whitespace-pre-line">
                                    {d.personalized_message}
                                </p>
                            )}

                            {/* Encart Message Direction */}
                            {d.direction_message && (
                                <div className="rounded-lg bg-indigo-50 border border-indigo-200 p-3">
                                    <div className="flex items-start gap-2">
                                        <Sparkles className="w-4 h-4 text-indigo-600 shrink-0 mt-0.5" />
                                        <div className="text-xs text-indigo-900 leading-relaxed whitespace-pre-line">
                                            <strong className="font-semibold">Message Direction · </strong>
                                            {d.direction_message}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Action attendue */}
                            {d.expected_action && (
                                <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-1.5">
                                    <strong>Action attendue :</strong> {d.expected_action}
                                </div>
                            )}

                            {/* Progression dossiers */}
                            {hasProspects && (
                                <div className="space-y-1.5">
                                    <div className="flex items-center justify-between text-xs text-slate-600">
                                        <span>
                                            <strong className="text-slate-900">{d.prospect_count_moved || 0}</strong>
                                            {' / '}
                                            <strong className="text-slate-900">{d.prospect_count}</strong> dossier{d.prospect_count > 1 ? 's' : ''} traité{d.prospect_count > 1 ? 's' : ''}
                                        </span>
                                        {progressPct !== null && <span className="font-semibold text-purple-700">{progressPct}%</span>}
                                    </div>
                                    <div className="h-1.5 bg-purple-100 rounded-full overflow-hidden">
                                        <div
                                            className="h-full bg-purple-600 transition-all duration-500"
                                            style={{ width: `${progressPct || 0}%` }}
                                        />
                                    </div>
                                </div>
                            )}

                            {/* Actions */}
                            <div className="flex flex-wrap items-center gap-2 pt-1">
                                {hasProspects && (
                                    <Button
                                        type="button"
                                        size="sm"
                                        variant={isViewed ? 'outline' : 'default'}
                                        onClick={() => openMyProspects(d)}
                                        data-testid={`directive-view-prospects-${d.mission_id}`}
                                        className={isViewed
                                            ? 'border-purple-300 text-purple-700 hover:bg-purple-50'
                                            : 'bg-purple-600 hover:bg-purple-700 text-white'
                                        }
                                    >
                                        <FileText className="w-4 h-4 mr-1.5" />
                                        {isViewed ? 'Voir à nouveau' : `Voir mes ${d.prospect_count_remaining || d.prospect_count} dossier${d.prospect_count > 1 ? 's' : ''}`}
                                        <ChevronRight className="w-4 h-4 ml-1" />
                                    </Button>
                                )}

                                <Button
                                    type="button"
                                    size="sm"
                                    variant="outline"
                                    disabled={!canAck || isAcking}
                                    onClick={() => ackDirective(d)}
                                    data-testid={`directive-ack-${d.mission_id}`}
                                    className={canAck ? 'border-emerald-400 text-emerald-800 hover:bg-emerald-50' : ''}
                                    title={!canAck ? 'Regarde d\'abord tes dossiers concernés' : ''}
                                >
                                    {isAcking ? (
                                        <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                                    ) : (
                                        <Check className="w-4 h-4 mr-1.5" />
                                    )}
                                    J'ai lu et compris
                                </Button>

                                {hasProspects && !isViewed && (
                                    <span className="text-[11px] text-slate-500 italic">
                                        Ouvre tes dossiers avant d'accuser réception.
                                    </span>
                                )}
                            </div>
                        </div>
                    );
                })}
            </CardContent>
        </Card>
    );
}
